import { useCallback, useContext } from "react";
import {
  useFlag,
  useFlagsStatus,
  useUnleashContext,
  useUnleashClient,
} from "@unleash/proxy-client-react";
import { addFeatureFlagEvaluation } from "../o11y";
import { ContextProps, ArenaContext } from "../provider";

/**
 * The react provider and the raw js client use the `ready` term to mean different things.
 * The react status means that the provider is set up to respond to toggle requests
 * The client status means it was actually able to contact the network and get the flags
 * We need both to be true to mean that the client is ready to use.
 */
function useIsClientReady() {
  const { flagsReady } = useFlagsStatus();
  const client = useUnleashClient();

  return flagsReady && client.isReady();
}

function useArenaContext(): ContextProps {
  const context = useContext(ArenaContext);
  if (!context) {
    throw new Error(
      "This hook must be used within a FeatureFlagProvider from @jsq/flags-react",
    );
  }

  return context;
}

/**
 * Check if a flag is enabled or not in Unleash. Please note that we do not support variant flags yet.
 * It is highly recommended to create a wrapping hook for each specific flag so that all evaluations
 * use the same `defaultValue`. See the `@jsq/flags-react` README for more information.
 * @param flagKey The flag key
 * @param defaultValue The default value to return if the client is not ready
 * @returns Whether the flag is enabled or not
 */
export function useFeatureFlag(flagKey: string, defaultValue: boolean): boolean {
  const isReady = useIsClientReady();
  const isEnabled = useFlag(flagKey);

  if (!isReady) return defaultValue;

  addFeatureFlagEvaluation(flagKey, isEnabled);

  return isEnabled;
}

// small utility function to make it easier to update the context properties
// since for our use case, we will always be interacting with properties
/**
 * A hook to update the context properties. This is useful for updating additional context that we want to base flag evaluations on.
 * This hook implies advanced usage, so if you find yourself using it, please reach out the platform team to confirm your use case.
 * @returns A function that takes a properties object and updates the context. Property keys should always be camelCase.
 */
export function useUpdateContext() {
  const updateContext = useUnleashContext();

  const updateCallback = useCallback(
    async (properties: Record<string, string>) => {
      await updateContext({ properties });
    },
    [updateContext],
  );
  return updateCallback;
}

/**
 * Identify the arena that flag evaluations should be based on. If you need to unset the arena, call the hook with null or undefined.
 * @returns A function that takes an arenaId and updates the context.
 */
export function useIdentifyArena() {
  const updateContext = useUpdateContext();
  // updating the arenaId will cause all flags to re-evaluate, so
  // we store the current arenaId in the context to prevent
  // needless re-renders if someone updates it to the same value
  const { arenaId: currentArenaId, setArenaId } = useArenaContext();

  const identifyArena = useCallback(
    async (arenaId?: string | number | null | undefined): Promise<void> => {
      const normalizedArenaId = arenaId || arenaId === 0 ? String(arenaId) : "";
      if (normalizedArenaId === currentArenaId) return Promise.resolve();

      await updateContext({ arenaId: normalizedArenaId }).then(() => {
        setArenaId(normalizedArenaId);
      });
    },
    [currentArenaId, setArenaId, updateContext],
  );
  return identifyArena;
}
